// src/lib/supabaseAuth.js
//
// Wave 3b (part 1) — NEW, ADDITIVE FILE. Replacement for the Firebase Auth
// calls used today across App.js / LoginPage.js / ClientDashboard.js /
// KnowledgeBasePage.js / BostaPage.js / WebhookSetupPage.js (see
// src/lib/MIGRATION_MAP.md for the exact file:line of every call site this
// module is meant to eventually replace). None of those files import this
// one yet — zero behavior change today.
//
// ============================================================================
// THE SECURITY FIX THIS MODULE EXISTS TO DELIVER
// ============================================================================
// Today (Firebase), a brand's identity inside ClientDashboard.js is resolved
// three different ways depending on how the page was opened:
//   - `?uid=<firebase_uid>` in the URL (super-admin "view as" links AND the
//     normal Shopify-auto-login redirect both use this) — ClientDashboard.js
//     L3890-3897, App.js's `ShopifyDashboardHandler`/`DashboardWrapper`.
//   - `?shop=<domain>` in the URL, queried directly against `clients_api`
//     with NO Firebase sign-in at all (App.js L274-317, ClientDashboard.js
//     L3883-3889).
//   - `user.uid` from a real `onAuthStateChanged` Firebase session (the only
//     one of the three that is actually cryptographically tied to a login).
// The first two are exactly the "uid-in-URL" vulnerability flagged in this
// project's own docs (DAF_CONFIRMATION_CONTEXT.md, Firestore rules section):
// nothing stops a user from typing `?uid=<any other brand's uid>` into the
// address bar and viewing/controlling that brand's dashboard, because the
// current Firestore rules are read-open and nothing server-side validates
// that the *caller* is entitled to that uid.
//
// This module closes that hole **by construction**: every function below
// resolves "which brand am I" from `supabase.auth.getUser()` — i.e. the
// GoTrue session's JWT `sub` claim — and lets Postgres RLS
// (`daf_confirmation.owns_brand()`, see daf_confirmation_rls.sql) do the
// actual matching server-side. There is no `uid`/`admin` query-param
// resolution path in this file, on purpose. If a wiring layer built on top
// of this module ever needs a "view any brand" admin feature, it must call
// `isSuperAdmin()` below and then hit a *dedicated, service-role-backed*
// endpoint that explicitly authorizes the impersonation — never trust a URL
// param again. That endpoint doesn't exist yet; see "Known gaps" at the
// bottom of this file and in MIGRATION_MAP.md.
// ============================================================================

import { supabase } from '../supabaseClient';

// ----------------------------------------------------------------------------
// Session primitives — replace onAuthStateChanged / signInWithEmailAndPassword
// / signOut from 'firebase/auth'.
// ----------------------------------------------------------------------------

/**
 * Replaces `signInWithEmailAndPassword(auth, email, password)`
 * (LoginPage.js L23). GoTrue password-grant login.
 * @returns {Promise<{data: {user, session}|null, error: import('@supabase/supabase-js').AuthError|null}>}
 */
export async function signInWithPassword(email, password) {
  return supabase.auth.signInWithPassword({ email, password });
}

/**
 * Replaces `signOut(auth)` (ClientDashboard.js `handleLogout`,
 * WebhookSetupPage.js L19).
 */
export async function signOut() {
  return supabase.auth.signOut();
}

/**
 * Sends a GoTrue magic-link email the merchant can click to sign in without
 * a password — needed for accounts auto-provisioned by the Daf One →
 * Daf Confirmation bridge (`confirmation-provision.js`), which never set a
 * password the merchant knows. This is a THIN wrapper over the standard
 * self-service `signInWithOtp` flow (client anon key only, no service-role
 * involved) — deliberately NOT the same code path as
 * `consumeMagicLinkToken()`'s server-minted `verifyOtp({type:'magiclink'})`
 * above: that one *consumes* a token an Express route already generated via
 * the GoTrue Admin API; this one *requests* GoTrue send a brand-new one
 * directly to the merchant's inbox.
 *
 * `emailRedirectTo` is passed explicitly, pointing at this dashboard's own
 * `/auth` route (App.js's `MagicLinkHandler` — same handler the
 * server-minted flow already uses).
 *
 * ⚠️ VERIFIED LIVE (2026-07-07, via a throwaway `admin.generateLink` probe
 * against a nonexistent test email — zero real user touched, cleaned up
 * immediately): this stack's `GOTRUE_URI_ALLOW_LIST`
 * (`ADDITIONAL_REDIRECT_URLS` in `daf inventory/supabase/.env`) is currently
 * EMPTY, which makes GoTrue silently ignore ANY `redirect_to`/
 * `emailRedirectTo` that isn't `GOTRUE_SITE_URL` itself
 * (`https://supabase-daf-inventory.dafaiagency.shop`) and fall back to that
 * Supabase URL instead — confirmed with both a cross-subdomain redirect_to
 * AND a same-apex-domain one, both got silently rewritten. Passing this
 * dashboard's URL here is still the *correct* code (harmless today, and it
 * starts working with ZERO further code changes once the owner adds this
 * exact string to `ADDITIONAL_REDIRECT_URLS`), but until that .env change +
 * an `dafinv-auth` container restart happens, an emailed magic-link will
 * land the merchant on the Supabase project's own domain, not this
 * dashboard. That .env/infra change is deliberately OUT OF SCOPE for this
 * task (shared with Daf One, not one of this task's 3 target files) — see
 * DAF_CONFIRMATION_CONTEXT.md's Timeline entry for this task.
 *
 * @param {string} email
 * @returns {Promise<{error: import('@supabase/supabase-js').AuthError|null}>}
 */
export async function sendMagicLinkEmail(email) {
  const { error } = await supabase.auth.signInWithOtp({
    email,
    options: {
      shouldCreateUser: false,
      emailRedirectTo: `${window.location.origin}/auth`,
    },
  });
  return { error };
}

/**
 * Replaces `onAuthStateChanged(auth, (user) => {...})`
 * (App.js L63, KnowledgeBasePage.js L49, BostaPage.js L43).
 *
 * Firebase's callback receives a `user` object (or null); GoTrue's receives
 * `(event, session)`. This wrapper normalizes to the same shape the old
 * call sites expect — `callback(user)` where `user` is
 * `session?.user ?? null` — so a future wiring pass can swap the import and
 * change as little call-site code as possible.
 *
 * @param {(user: import('@supabase/supabase-js').User | null) => void} callback
 * @returns {() => void} unsubscribe function (same calling convention as the
 *   Firebase `onAuthStateChanged` return value — old code always does
 *   `const unsubscribe = onAuthStateChanged(...); return () => unsubscribe();`
 *   which keeps working unchanged if this function is swapped in, since we
 *   return a plain callable here too, not `{ data: { subscription } }`).
 */
export function onAuthStateChange(callback) {
  const { data } = supabase.auth.onAuthStateChange((_event, session) => {
    callback(session?.user ?? null);
  });
  // Fire once immediately with the current session, matching
  // onAuthStateChanged's behavior of firing synchronously-ish on attach with
  // whatever the current auth state is (Firebase does this too — it's not
  // pure event-driven, the first callback reflects "on mount" state).
  supabase.auth.getSession().then(({ data: sessionData }) => {
    callback(sessionData.session?.user ?? null);
  });
  return () => data.subscription.unsubscribe();
}

/** Raw session getter — for one-off checks (not a subscription). */
export async function getSession() {
  const { data, error } = await supabase.auth.getSession();
  return { session: data?.session ?? null, error };
}

/**
 * Exchanges a single-use GoTrue magic-link token (minted server-side by the
 * Shopify app / super-admin impersonation endpoint via
 * `services/gotrue.js`'s `generateMagicLinkToken`) for a real GoTrue
 * session. This is the safe replacement for the old `?uid=<auth_uid>`
 * "seamless login" redirect: the browser never carries a permanent
 * identifier the dashboard blindly trusts — only a short-lived, single-use
 * token that GoTrue itself validates and burns on first use.
 *
 * Verified live against the real GoTrue instance (2026-07-06): a valid,
 * unused token resolves to `{ session: { access_token, refresh_token, ... },
 * user }`; a missing/expired/already-used token resolves to
 * `error.message === "Email link is invalid or has expired"`. Callers MUST
 * treat any error as "fall back to the normal login page" — never render
 * the dashboard on a failed exchange.
 *
 * @param {string} tokenHash - the `hashed_token` from `generate_link`.
 * @returns {Promise<{session: import('@supabase/supabase-js').Session|null, error: Error|null}>}
 */
export async function consumeMagicLinkToken(tokenHash) {
  const { data, error } = await supabase.auth.verifyOtp({
    token_hash: tokenHash,
    type: 'magiclink',
  });
  if (error) return { session: null, error };
  return { session: data?.session ?? null, error: null };
}

/**
 * Raw user getter. Unlike `getSession()` (which reads the locally cached
 * JWT), this round-trips to GoTrue to re-validate the token — use this,
 * not `getSession()`, whenever the result is about to gate a write or an
 * ownership decision, per Supabase's own guidance (`getSession()` is fine
 * for "am I logged in enough to show a UI", not for "is this identity
 * trustworthy enough to act on").
 */
export async function getUser() {
  const { data, error } = await supabase.auth.getUser();
  return { user: data?.user ?? null, error };
}

// ----------------------------------------------------------------------------
// Brand identity + settings — replaces the giant `clientQuery` effect at
// ClientDashboard.js L3879-3934 (the one keyed on shopifyShop / viewingClientUid
// / user.uid) AND every `query(collection(db,'clients_api'), where('auth_uid',
// '==', clientInfo.auth_uid))` + `getDocs` + `updateDoc` pair scattered across
// the settings-toggle handlers (L1519-1884). See MIGRATION_MAP.md for the full
// per-handler mapping.
//
// Deliberate design choice per the task brief: NO client-side `.from(...)`
// query for "my brand" ever takes a uid/brand-name parameter from the
// caller — everything is resolved server-side by the RPCs below, which
// derive the brand from `auth.uid()` inside Postgres. This is what makes
// the uid-in-URL class of bug structurally impossible here: even if a
// malicious caller invoked `getMyBrand()` with a tampered arguments object
// (there are none to tamper — the functions take zero identity params),
// the RPC only ever returns/updates the row whose `data->>'auth_uid'`
// equals the *session's* `auth.uid()`.
// ----------------------------------------------------------------------------

/**
 * Fetches the caller's own brand row (typed columns + a fixed whitelist of
 * `data` fields — see `daf_confirmation.get_my_brand_settings()` in
 * daf_confirmation_rls.sql for the exact whitelist). Returns `null` if the
 * caller has no session, or no `clients_api` row's `auth_uid` matches the
 * session yet (this will be true for every real brand until the GoTrue
 * provisioning cutover — see the header comment in supabaseClient.js).
 *
 * Replaces: the `clientInfo` state populated by ClientDashboard.js's
 * `clientQuery` onSnapshot effect (L3879-3934), for the "regular logged-in
 * merchant" branch specifically (`user?.uid` — L3899-3905). The
 * `shopifyShop`/`viewingClientUid` branches are NOT reproduced here — see
 * "Known gaps" below.
 *
 * @returns {Promise<{brand: object|null, error: Error|null}>}
 */
export async function getMyBrand() {
  const { data, error } = await supabase.rpc('get_my_brand_settings');
  if (error) return { brand: null, error };
  return { brand: data ?? null, error: null };
}

/**
 * Patches whitelisted settings fields on the caller's own brand. This is
 * the single write path for every "toggle a setting" handler in
 * ClientDashboard.js — see MIGRATION_MAP.md for the exhaustive list
 * (handleToggleAutoCancel, handleToggleDuplicatedOrderNote,
 * handleCancelTimeoutChange, handleFirstMsgDelayChange,
 * handleToggleDeliveryDate, handleDeliveryDaysChange, handleToggleFeedback,
 * handleToggleShippingCheck, handleToggleBostaActions, handleSaveInfo).
 *
 * The whitelist is enforced INSIDE Postgres
 * (`daf_confirmation.update_my_brand_settings`), not just here — passing an
 * unknown key is silently dropped server-side (not an error, unless the
 * patch becomes empty after filtering, in which case the RPC raises
 * `no_allowed_fields_in_patch`). This function does not attempt to
 * duplicate that whitelist client-side on purpose: the server is the only
 * place that whitelist may safely live, so there is exactly one place to
 * update it if the merchant-editable field set ever changes.
 *
 * ⚠️ KNOWN GAP — `ai_mode` / `order_cost`: ClientDashboard.js's
 * `handleToggleAiMode` (L1754-1813) writes BOTH `ai_mode` and `order_cost`
 * in the same Firestore update (order_cost flips between
 * `order_cost_no_ai`/`order_cost_ai` depending on the new ai_mode value).
 * `order_cost` is a **typed, billing-relevant column** on `clients_api`
 * (not a `data` field) and is deliberately NOT merchant-writable under the
 * live Wave 3 grants (`update_my_brand_settings`'s whitelist excludes it,
 * and there is no direct UPDATE grant on the column either — see
 * daf_confirmation_rls.sql §1). Calling `updateMyBrandSettings({ai_mode:
 * true, order_cost: 0.06})` will silently drop `order_cost` from the patch
 * (it's not in the whitelist) — `ai_mode` will be saved, `order_cost` will
 * NOT. This is flagged prominently in MIGRATION_MAP.md as an open decision
 * for the owner: either (a) extend the RPC whitelist to allow order_cost
 * (re-introduces client-trust over a billing-relevant field — the exact
 * regression the secrets/typed-column split was designed to avoid), or (b)
 * compute+write order_cost server-side (a tiny dedicated RPC/endpoint that
 * takes only `{ai_mode: boolean}` and derives order_cost from the brand's
 * own `order_cost_no_ai`/`order_cost_ai` fields inside Postgres — the
 * correct fix, not implemented here since it needs a new SQL function this
 * task's scope doesn't include).
 *
 * @param {object} patch - subset of the whitelisted keys, see
 *   daf_confirmation_rls.sql's `update_my_brand_settings` for the exact list.
 * @returns {Promise<{brand: object|null, error: Error|null}>} the fresh
 *   settings snapshot (RPC returns `get_my_brand_settings()` after writing),
 *   so callers can update local state directly from the response instead of
 *   re-fetching.
 */
export async function updateMyBrandSettings(patch) {
  const { data, error } = await supabase.rpc('update_my_brand_settings', {
    p_patch: patch,
  });
  if (error) return { brand: null, error };
  return { brand: data ?? null, error: null };
}

/**
 * Replaces the Firebase-era pattern of trusting an `?admin=` URL param
 * (App.js `isSuperAdminView = !!adminUidFromUrl` — never itself verified
 * against anything). Calls the SECURITY DEFINER
 * `daf_confirmation.is_super_admin()` RPC, which checks the *session's*
 * `auth.uid()` against `super_admins.data->>'auth_uid'` — a super-admin
 * claim can never be forged by a query string under this design.
 *
 * @returns {Promise<{isSuperAdmin: boolean, error: Error|null}>}
 */
export async function isSuperAdmin() {
  const { data, error } = await supabase.rpc('is_super_admin');
  if (error) return { isSuperAdmin: false, error };
  return { isSuperAdmin: data === true, error: null };
}

// ============================================================================
// KNOWN GAPS — UPDATE (2026-07-06, seamless-login task): #1 and #2 below are
// now CLOSED via the GoTrue magic-link flow (`consumeMagicLinkToken()` above
// + the dashboard's new `/auth?token=...` route in App.js). Both the Shopify
// seamless-login redirect (getDashboardUrl() in confirming_cli/daf-agency/
// web/index.js) and the super-admin "view as brand" button
// (POST /api/dashboard/impersonateBrand in routes/dashboard-api.js) now mint
// a single-use GoTrue token server-side (service_role Admin API, only AFTER
// the caller's own identity/authorization is verified) and hand the browser
// `/auth?token=...` instead of `/dashboard?uid=...`/`?admin=&uid=`. Neither
// flow needed a new SQL RPC — the impersonation endpoint reuses the existing
// `is_super_admin()` check (via services/db.js's `isSuperAdmin(authUid)`)
// purely as a gate before minting the target brand's link; once the browser
// has a real session for that brand, ordinary `getMyBrand()`/RLS resolve
// everything exactly as they do for a normal login. Kept the original
// gap descriptions below for history/context.
//
// KNOWN GAPS (original text, now resolved — see update above):
//
// 1. Super-admin "view any brand" — the live product has a real feature
//    (super-admin picks a brand from a list and views/edits its dashboard
//    as if logged in as that brand — driven today by `?admin=&uid=`). This
//    module deliberately provides no client-side "fetch brand X by name"
//    call for that flow (`get_my_brand_settings()` only ever resolves the
//    CALLER's own brand via `auth.uid()`, it has no brand-name parameter to
//    misuse). Reproducing the admin "view as" feature safely needs a NEW,
//    separate RPC (e.g. `daf_confirmation.get_brand_settings_as_admin(text)`
//    that internally calls `is_super_admin()` before doing a plain
//    brand_name/doc_id lookup) — that RPC does not exist yet in
//    daf_confirmation_rls.sql and is out of this task's scope (additive
//    files only, zero new SQL). Do NOT bolt this onto
//    `get_my_brand_settings()` — keep the "my own brand" and "any brand as
//    admin" code paths structurally separate so the former can never
//    regress into the latter by accident.
//
// 2. Shopify-domain auto-login (`?shop=` with no session at all,
//    App.js L270-317 / ClientDashboard.js L3883-3889) — this is the other
//    half of the uid-in-URL vulnerability and is intentionally NOT
//    reproduced here. A real fix needs the merchant's browser to hold an
//    actual GoTrue session before the dashboard renders anything — e.g. by
//    minting one server-side (service_role) right after Shopify OAuth
//    completes and handing the merchant a real session (same shape as DAF
//    One's `daf.session` HttpOnly JWT cookie pattern, or GoTrue's own
//    magic-link/OTP flow) instead of a bare `?shop=` query string the
//    client trusts unconditionally. Building that is server-side work
//    (Express gateway) outside this task's "additive dashboard files only"
//    scope — see MIGRATION_MAP.md, "5-callable plan" section, which folds
//    this into the `completeShopifyOnboarding` redesign.
//
// 3. Bosta API key (`clients_api.data.bosta_api`, BostaPage.js L87-90) and
//    the three webhook shared-secrets (`shopify_shared_secret` /
//    `easyorder_shared_secret` / `easyorder_feedback_secret`,
//    WebhookSetupPage.js L96-108) are all in the explicit SECRETS list
//    inside daf_confirmation_rls.sql's `clients_api` comment block — they
//    are excluded from BOTH the column-level SELECT grant and the
//    `update_my_brand_settings` whitelist on purpose. There is no RPC in
//    this file (or planned in confirmationData.js) that can read or write
//    them. These three flows need dedicated server-side endpoints (service
//    role), documented in MIGRATION_MAP.md.
// ============================================================================
